import React, { FormEvent, useMemo, useState } from 'react';
import { AlertTriangle, ArrowLeft, Calculator, CheckCircle2, Disc3, Download, Info, Loader2, ShieldAlert, Sliders, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL, apiPost, getJWT } from '../auth';
import SteelCoilTechnicalDrawing from '../features/steel-coil/visualization/SteelCoilTechnicalDrawing';
import SteelCoil3DCard from '../features/steel-coil/visualization/SteelCoil3DCard';

type DirectionResult = {
  direction: 'forward' | 'rearward' | 'left' | 'right';
  force: {
    external_force_n: number;
    friction_resistance_n: number;
    remaining_force_n: number;
  };
  tipping: {
    securing_required: boolean;
  };
  half_loop_capacity_t: number;
  structural_blocking: {
    required_force_kn: number;
    total_verified_capacity_kn: number | null;
    utilization: number | null;
    capacity_verified: boolean;
    passes: boolean;
    governing_check: string;
  } | null;
  direct_lashing: {
    lashing_count: number;
    effective_msl_n: number | null;
    total_directional_capacity_n: number | null;
    utilization: number | null;
    capacity_verified: boolean;
    passes: boolean;
    governing_component: string;
  } | null;
  direct_lashing_tipping: {
    overturning_moment_knm: number;
    natural_restoring_moment_knm: number;
    required_lashing_moment_knm: number;
    total_lashing_moment_knm: number | null;
    residual_moment_knm: number | null;
    utilization: number | null;
    capacity_verified: boolean;
    passes: boolean;
  } | null;
  sliding_status: string;
  tipping_status: string;
};

type CalculationResult = {
  bedding: {
    required_length_m: number;
    minimum_length_cm: number;
    governing_check: string;
    kaps: { transverse_strength_length_m: number; longitudinal_strength_length_m: number };
  };
  chock: { required_height_m: number; provided_height_m: number; geometry_passes: boolean };
  bottom_blocking: { required_height_m: number; provided_height_m: number; geometry_passes: boolean };
  directions: DirectionResult[];
  warnings: string[];
  engineering_review_required: boolean;
};

const initialForm = {
  coil_mass_kg: 10000,
  container_payload_kg: 28000,
  transport_profile: 'road',
  transport_unit: 'container_20',
  coil_axis: 'longitudinal',
  outer_diameter_m: 1.8,
  inner_diameter_m: 0.6,
  coil_width_m: 1.2,
  friction_coefficient: 0.3,
  friction_surface: '',
  beam_spread_m: 1.0,
  provided_chock_height_m: 0.3,
  provided_bottom_blocking_height_m: 0.05,
  cog_vertical_distance_m: 0.9,
  cog_longitudinal_distance_m: 0.6,
  cog_transverse_distance_m: 0.5,
  use_geometric_cog: false,
  half_loop_pair_count: 2,
  certified_half_loop_msl_per_lashing_n: 20000,
  cordstrap_product: '',
  blocking_block_width_cm: 10,
  blocking_block_height_cm: 10,
  blocking_count_forward: 0,
  blocking_count_rearward: 0,
  blocking_count_left: 0,
  blocking_count_right: 0,
  allowable_section_stress_kn_per_cm2: 0,
  attachment_capacity_kn_per_block: 0,
  direct_lashing_count_forward: 0,
  direct_lashing_count_rearward: 0,
  direct_lashing_count_left: 0,
  direct_lashing_count_right: 0,
  direct_lashing_msl_n: 0,
  cargo_attachment_msl_n: 0,
  ctu_attachment_msl_n: 0,
  direct_lashing_vertical_angle_deg: 45,
  direct_lashing_horizontal_angle_deg: 45,
  direct_lashing_attachment_height_m: 0.9,
  direct_lashing_attachment_lever_m: 0.5,
  lashings_prevent_roll_over: false,
  lashings_prevent_climbing: false,
  carrier_rules: ['tt_club_cins_4500', 'hapag_hdi_4800'],
};

const labels: Record<string, string> = {
  forward: 'İleri', rearward: 'Geri', left: 'Sol', right: 'Sağ',
};

const statusLabel = (status: string) => ({
  friction_only_pass: 'Sürtünme yeterli',
  half_loop_table_pass: 'Half-loop yeterli',
  structural_blocking_pass: 'Bloklama yeterli',
  direct_lashing_pass: 'Doğrudan lashing yeterli',
  additional_securing_required: 'Ek sabitleme gerekli',
}[status] || status);

const Field = ({ label, unit, children }: { label: string; unit?: string; children: React.ReactNode }) => (
  <label className="block">
    <span className="mb-1 flex items-center justify-between text-xs font-semibold text-slate-600 dark:text-slate-300">
      {label}{unit && <span className="font-normal text-slate-400">{unit}</span>}
    </span>
    {children}
  </label>
);

const inputClass = 'h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-500/15 dark:border-slate-600 dark:bg-[#202c42] dark:text-white';

export default function SteelCoilCalculator() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<'quick' | 'advanced'>('quick');
  const [form, setForm] = useState(initialForm);
  const [result, setResult] = useState<CalculationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [error, setError] = useState('');

  const setValue = (key: keyof typeof initialForm, value: unknown) => {
    setForm(current => ({ ...current, [key]: value }));
    setResult(null);
    setError('');
  };

  const numeric = (key: keyof typeof initialForm) => ({
    value: form[key] as number,
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => setValue(key, Number(event.target.value)),
  });

  const handleOuterDiameterChange = (val: number) => {
    setForm(current => {
      const next = { ...current, outer_diameter_m: val };
      if (mode === 'quick') {
        if (next.inner_diameter_m >= val) {
          next.inner_diameter_m = Number((val * 0.35).toFixed(2));
        }
        const recommendedSpread = Number(Math.max(1.0, val * 0.6).toFixed(2));
        if (recommendedSpread < 2.25) {
          next.beam_spread_m = recommendedSpread;
        }
        next.use_geometric_cog = true;
      }
      return next;
    });
    setResult(null);
    setError('');
  };

  const localError = useMemo(() => {
    if (form.coil_mass_kg > form.container_payload_kg) return 'Coil ağırlığı konteyner payload değerini aşamaz.';
    if (form.inner_diameter_m >= form.outer_diameter_m) return 'İç çap dış çaptan küçük olmalıdır.';
    if (form.beam_spread_m >= 2.26) return 'Bedding açıklığı 2,26 m değerinden küçük olmalıdır.';
    if (form.half_loop_pair_count > 0 && !form.cordstrap_product && form.certified_half_loop_msl_per_lashing_n <= 0) return 'Half-loop için sertifikalı MSL gereklidir.';
    const directCount = form.direct_lashing_count_forward + form.direct_lashing_count_rearward + form.direct_lashing_count_left + form.direct_lashing_count_right;
    if (directCount > 0 && ((!form.cordstrap_product && form.direct_lashing_msl_n <= 0) || form.cargo_attachment_msl_n <= 0 || form.ctu_attachment_msl_n <= 0)) return 'Doğrudan lashing için Cordstrap ürün kapasitesi veya manuel lashing MSL ile yük ve CTU bağlantısı sertifikalı MSL değerleri gereklidir.';
    return '';
  }, [form]);

  const assessmentPasses = result ? result.chock.geometry_passes
    && result.bottom_blocking.geometry_passes
    && result.directions.every(direction => direction.sliding_status !== 'additional_securing_required' && direction.tipping_status !== 'additional_securing_required')
    : undefined;
  const failedDirectionCount = result?.directions.filter(direction => direction.sliding_status === 'additional_securing_required' || direction.tipping_status === 'additional_securing_required').length || 0;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (localError) return;
    setLoading(true);
    setError('');
    try {
      const data = await apiPost<CalculationResult>('/api/steel-coil/calculate', {
        ...form,
        allowable_section_stress_kn_per_cm2: form.allowable_section_stress_kn_per_cm2 || null,
        attachment_capacity_kn_per_block: form.attachment_capacity_kn_per_block || null,
        friction_coefficient: form.friction_surface ? null : form.friction_coefficient,
        cog_vertical_distance_m: form.use_geometric_cog ? null : form.cog_vertical_distance_m,
        cog_longitudinal_distance_m: form.use_geometric_cog ? null : form.cog_longitudinal_distance_m,
        cog_transverse_distance_m: form.use_geometric_cog ? null : form.cog_transverse_distance_m,
        direct_lashing_msl_n: form.cordstrap_product ? null : form.direct_lashing_msl_n || null,
        certified_half_loop_msl_per_lashing_n: form.cordstrap_product ? null : form.certified_half_loop_msl_per_lashing_n || null,
        cargo_attachment_msl_n: form.cargo_attachment_msl_n || null,
        ctu_attachment_msl_n: form.ctu_attachment_msl_n || null,
      });
      setResult(data);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Hesaplama tamamlanamadı.');
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    if (!result) return;
    setPdfLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/steel-coil/pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getJWT() || ''}` },
        body: JSON.stringify({
          ...form,
          allowable_section_stress_kn_per_cm2: form.allowable_section_stress_kn_per_cm2 || null,
          attachment_capacity_kn_per_block: form.attachment_capacity_kn_per_block || null,
          friction_coefficient: form.friction_surface ? null : form.friction_coefficient,
          cog_vertical_distance_m: form.use_geometric_cog ? null : form.cog_vertical_distance_m,
          cog_longitudinal_distance_m: form.use_geometric_cog ? null : form.cog_longitudinal_distance_m,
          cog_transverse_distance_m: form.use_geometric_cog ? null : form.cog_transverse_distance_m,
          direct_lashing_msl_n: form.cordstrap_product ? null : form.direct_lashing_msl_n || null,
          certified_half_loop_msl_per_lashing_n: form.cordstrap_product ? null : form.certified_half_loop_msl_per_lashing_n || null,
          cargo_attachment_msl_n: form.cargo_attachment_msl_n || null,
          ctu_attachment_msl_n: form.ctu_attachment_msl_n || null,
        }),
      });
      if (!response.ok) throw new Error('PDF raporu oluşturulamadı.');
      const url = URL.createObjectURL(await response.blob());
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `steel-coil-assessment-${new Date().toISOString().slice(0, 10)}.pdf`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (pdfError) {
      setError(pdfError instanceof Error ? pdfError.message : 'PDF raporu oluşturulamadı.');
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900 dark:bg-[#111827] dark:text-slate-100">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur dark:border-slate-700 dark:bg-[#182235]/95">
        <div className="mx-auto flex h-16 max-w-[1600px] items-center justify-between px-4 lg:px-6">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/app')} title="Lashing Calculator'a dön" className="grid h-9 w-9 place-items-center rounded-md text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <Disc3 className="h-7 w-7 text-orange-500" />
            <div>
              <h1 className="text-base font-bold tracking-normal !text-slate-900 dark:!text-white sm:text-lg">Steel Coil Calculator</h1>
              <p className="text-[11px] text-slate-500">CTU Code tabanlı mühendislik değerlendirmesi</p>
            </div>
          </div>
          <span className="hidden rounded-md border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800 sm:block dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-300">V14 · Engineering review</span>
        </div>
      </header>

      <main className="mx-auto grid max-w-[1600px] gap-5 p-4 lg:grid-cols-[420px_minmax(0,1fr)] lg:p-6">
        <form onSubmit={submit} className="self-start rounded-lg border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-[#182235]">
          <div className="border-b border-slate-200 p-3 dark:border-slate-700">
            <div className="grid grid-cols-2 gap-1 rounded-lg bg-slate-100 p-1 dark:bg-[#111827]">
              <button
                type="button"
                onClick={() => {
                  setMode('quick');
                  setValue('use_geometric_cog', true);
                }}
                className={`flex items-center justify-center gap-1.5 rounded-md py-2 text-xs font-bold transition-all ${
                  mode === 'quick'
                    ? 'bg-white text-orange-600 shadow-sm dark:bg-[#1e293b] dark:text-orange-400'
                    : 'text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white'
                }`}
              >
                <Zap className="h-3.5 w-3.5" />
                <span>Hızlı Mod (6 Alan)</span>
              </button>
              <button
                type="button"
                onClick={() => setMode('advanced')}
                className={`flex items-center justify-center gap-1.5 rounded-md py-2 text-xs font-bold transition-all ${
                  mode === 'advanced'
                    ? 'bg-white text-orange-600 shadow-sm dark:bg-[#1e293b] dark:text-orange-400'
                    : 'text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white'
                }`}
              >
                <Sliders className="h-3.5 w-3.5" />
                <span>Gelişmiş Mod</span>
              </button>
            </div>
          </div>

          {mode === 'quick' ? (
            <div className="space-y-4 p-4">
              <div className="rounded-lg border border-orange-200/80 bg-gradient-to-r from-orange-50/80 to-amber-50/50 p-3 text-xs text-orange-950 dark:border-orange-900/40 dark:from-orange-950/20 dark:to-amber-950/10 dark:text-orange-200">
                <div className="flex items-center gap-1.5 font-bold text-orange-700 dark:text-orange-400">
                  <Zap className="h-4 w-4 shrink-0" />
                  <span>Saha &amp; Forwarder Hızlı Değerlendirmesi</span>
                </div>
                <p className="mt-1 text-[11px] leading-relaxed text-slate-600 dark:text-slate-300">
                  Temel 6 operasyonel parametre ile anında CTU Code kayma, devrilme ve yataklama analizi yapar.
                  Kesit gerilmesi veya açı analizi gibi ileri düzey mühendislik hesapları için yukarıdan <strong>Gelişmiş Mod</strong>'a geçebilirsiniz.
                </p>
              </div>

              {/* 1. Taşıma ve Konteyner */}
              <section className="space-y-3 rounded-lg border border-slate-200/80 bg-slate-50/50 p-3 dark:border-slate-700/60 dark:bg-slate-800/20">
                <h3 className="text-xs font-bold uppercase tracking-normal text-orange-600 dark:text-orange-400">1. Taşıma ve Konteyner</h3>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Taşıma profili">
                    <select className={inputClass} value={form.transport_profile} onChange={e => setValue('transport_profile', e.target.value)}>
                      <option value="road">Karayolu</option>
                      <option value="rail_combined">Demiryolu</option>
                      <option value="sea_a">Deniz A (Hs ≤ 8m)</option>
                      <option value="sea_b">Deniz B (8m &lt; Hs ≤ 12m)</option>
                      <option value="sea_c">Deniz C (Okyanus / Şiddetli)</option>
                    </select>
                  </Field>
                  <Field label="Taşıma ünitesi">
                    <select className={inputClass} value={form.transport_unit} onChange={e => setValue('transport_unit', e.target.value)}>
                      <option value="container_20">20 ft konteyner</option>
                      <option value="container_40">40 ft konteyner</option>
                      <option value="flatrack_20">20 ft flatrack</option>
                      <option value="flatrack_40">40 ft flatrack</option>
                      <option value="truck_trailer_standard">Standart treyler</option>
                      <option value="open_top_20">20 ft open top</option>
                    </select>
                  </Field>
                </div>
              </section>

              {/* 2. Bobin Ölçüleri */}
              <section className="space-y-3 rounded-lg border border-slate-200/80 bg-slate-50/50 p-3 dark:border-slate-700/60 dark:bg-slate-800/20">
                <h3 className="text-xs font-bold uppercase tracking-normal text-orange-600 dark:text-orange-400">2. Bobin (Coil) Bilgileri</h3>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Bobin ağırlığı" unit="kg">
                    <input type="number" min="100" step="50" className={inputClass} {...numeric('coil_mass_kg')} />
                  </Field>
                  <Field label="Bobin ekseni">
                    <select className={inputClass} value={form.coil_axis} onChange={e => setValue('coil_axis', e.target.value)}>
                      <option value="longitudinal">Boyuna (Eye-to-Door)</option>
                      <option value="transverse">Enine (Eye-to-Wall)</option>
                    </select>
                  </Field>
                  <Field label="Dış çap" unit="m">
                    <input
                      type="number"
                      min="0.1"
                      step="0.05"
                      className={inputClass}
                      value={form.outer_diameter_m}
                      onChange={e => handleOuterDiameterChange(Number(e.target.value))}
                    />
                  </Field>
                  <Field label="Bobin genişliği" unit="m">
                    <input type="number" min="0.1" step="0.05" className={inputClass} {...numeric('coil_width_m')} />
                  </Field>
                </div>
              </section>

              {/* 3. Zemin ve Sürtünme */}
              <section className="space-y-3 rounded-lg border border-slate-200/80 bg-slate-50/50 p-3 dark:border-slate-700/60 dark:bg-slate-800/20">
                <h3 className="text-xs font-bold uppercase tracking-normal text-orange-600 dark:text-orange-400">3. Zemin Teması / Sürtünme</h3>
                <Field label="Temas yüzeyi / sürtünme">
                  <select
                    className={inputClass}
                    value={form.friction_surface || (form.friction_coefficient === 0.3 ? 'metal_wood' : form.friction_coefficient === 0.6 ? 'asm' : '')}
                    onChange={e => {
                      const val = e.target.value;
                      setValue('friction_surface', val);
                      if (val === 'metal_wood') setValue('friction_coefficient', 0.3);
                      else if (val === 'asm') setValue('friction_coefficient', 0.6);
                      else if (val === 'metal_metal') setValue('friction_coefficient', 0.2);
                      else if (val === 'wood_wood') setValue('friction_coefficient', 0.45);
                    }}
                  >
                    <option value="metal_wood">Metal - Ahşap Yatak (µ = 0.30) · Standart</option>
                    <option value="asm">Kaymaz Mat / Kauçuk (µ = 0.60) · Güvenli</option>
                    <option value="wood_wood">Ahşap - Ahşap (µ = 0.45)</option>
                    <option value="metal_metal">Metal - Metal (µ = 0.20) · Kaygan</option>
                  </select>
                </Field>
              </section>

              {/* 4. Bağlama (Lashing) */}
              <section className="space-y-3 rounded-lg border border-slate-200/80 bg-slate-50/50 p-3 dark:border-slate-700/60 dark:bg-slate-800/20">
                <h3 className="text-xs font-bold uppercase tracking-normal text-orange-600 dark:text-orange-400">4. Bağlama Sistemi (Lashing)</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <Field label="Cordstrap kompozit ürün">
                      <select className={inputClass} value={form.cordstrap_product} onChange={e => setValue('cordstrap_product', e.target.value)}>
                        <option value="">Manuel Kayış / Standart Webbing</option>
                        <option value="CL1500:DYNA20">Cordstrap CL1500 DYNA20 (Ağır Yük)</option>
                        <option value="CL750:HDB15N">Cordstrap CL750 HDB15N</option>
                        <option value="CL200:HDB12">Cordstrap CL200 HDB12</option>
                        <option value="CL200:HDB12L">Cordstrap CL200 HDB12L</option>
                        <option value="CL150:HDB12L">Cordstrap CL150 HDB12L</option>
                        <option value="CL105:HDB10">Cordstrap CL105 HDB10</option>
                      </select>
                    </Field>
                  </div>
                  <Field label="Half-loop çift adedi">
                    <input type="number" min="0" max="6" step="1" className={inputClass} {...numeric('half_loop_pair_count')} />
                  </Field>
                  <Field label="Kayış MSL" unit="N">
                    <input
                      type="number"
                      min="1000"
                      step="1000"
                      disabled={!!form.cordstrap_product}
                      className={inputClass}
                      {...numeric('certified_half_loop_msl_per_lashing_n')}
                    />
                  </Field>
                </div>
              </section>

              {/* 5. Takoz ve Destek */}
              <section className="space-y-3 rounded-lg border border-slate-200/80 bg-slate-50/50 p-3 dark:border-slate-700/60 dark:bg-slate-800/20">
                <h3 className="text-xs font-bold uppercase tracking-normal text-orange-600 dark:text-orange-400">5. Takoz ve Destek</h3>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Takoz yüksekliği" unit="m">
                    <input type="number" min="0.05" step="0.01" className={inputClass} {...numeric('provided_chock_height_m')} />
                  </Field>
                  <Field label="Yataklama açıklığı" unit="m">
                    <input type="number" min="0.5" max="2.2" step="0.05" className={inputClass} {...numeric('beam_spread_m')} />
                  </Field>
                </div>
                <div className="mt-2 space-y-1.5 text-xs">
                  <label className="flex items-center gap-2 cursor-pointer text-slate-700 dark:text-slate-300">
                    <input
                      type="checkbox"
                      checked={form.lashings_prevent_roll_over}
                      onChange={e => setValue('lashings_prevent_roll_over', e.target.checked)}
                      className="rounded border-slate-300 text-orange-600 focus:ring-orange-500"
                    />
                    <span>Lashing kayışları takoz üzerinden yuvarlanmayı (rollover) engelliyor</span>
                  </label>
                </div>
              </section>

              {(localError || error) && (
                <div className="flex gap-2 rounded-md border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700 dark:border-rose-800 dark:bg-rose-950/30 dark:text-rose-300">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  {localError || error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !!localError}
                className="flex h-11 w-full items-center justify-center gap-2 rounded-md bg-orange-600 text-sm font-bold text-white transition hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calculator className="h-4 w-4" />}
                Hızlı Hesapla &amp; Doğrula
              </button>
            </div>
          ) : (
            <div className="space-y-5 p-4">
              <section>
                <h3 className="mb-3 text-xs font-bold uppercase tracking-normal text-orange-600">Taşıma ve yük</h3>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Taşıma profili"><select className={inputClass} value={form.transport_profile} onChange={e => setValue('transport_profile', e.target.value)}><option value="road">Karayolu</option><option value="rail_combined">Demiryolu</option><option value="sea_a">Deniz A</option><option value="sea_b">Deniz B</option><option value="sea_c">Deniz C</option></select></Field>
                  <Field label="Taşıma ünitesi"><select className={inputClass} value={form.transport_unit} onChange={e => setValue('transport_unit', e.target.value)}><option value="container_20">20 ft konteyner</option><option value="container_40">40 ft konteyner</option><option value="open_top_20">20 ft open top</option><option value="open_top_40">40 ft open top</option><option value="flatrack_20">20 ft flatrack</option><option value="flatrack_40">40 ft flatrack</option><option value="lowbed">Lowbed</option><option value="mafi">Mafi</option><option value="truck_trailer_standard">Standart treyler</option><option value="vessel">Gemi</option><option value="breakbulk">Breakbulk</option></select></Field>
                  <Field label="Coil ekseni"><select className={inputClass} value={form.coil_axis} onChange={e => setValue('coil_axis', e.target.value)}><option value="longitudinal">Boyuna</option><option value="transverse">Enine</option></select></Field>
                  <Field label="Coil ağırlığı" unit="kg"><input type="number" min="10" step="10" className={inputClass} {...numeric('coil_mass_kg')} /></Field>
                  <Field label="Konteyner payload" unit="kg"><input type="number" min="10" step="10" className={inputClass} {...numeric('container_payload_kg')} /></Field>
                </div>
              </section>

              <section>
                <h3 className="mb-3 text-xs font-bold uppercase tracking-normal text-orange-600">Doğrudan lashing</h3>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="İleri lashing adedi"><input type="number" min="0" step="1" className={inputClass} {...numeric('direct_lashing_count_forward')} /></Field>
                  <Field label="Geri lashing adedi"><input type="number" min="0" step="1" className={inputClass} {...numeric('direct_lashing_count_rearward')} /></Field>
                  <Field label="Sol lashing adedi"><input type="number" min="0" step="1" className={inputClass} {...numeric('direct_lashing_count_left')} /></Field>
                  <Field label="Sağ lashing adedi"><input type="number" min="0" step="1" className={inputClass} {...numeric('direct_lashing_count_right')} /></Field>
                  <Field label="Cordstrap ürünü"><select className={inputClass} value={form.cordstrap_product} onChange={e => setValue('cordstrap_product', e.target.value)}><option value="">Manuel sertifikalı MSL</option><option value="CL105:HDB10">CL105 HDB10</option><option value="CL150:HDB12L">CL150 HDB12L</option><option value="CL200:HDB12L">CL200 HDB12L</option><option value="CL200:HDB12">CL200 HDB12</option><option value="CL750:HDB15N">CL750 HDB15N</option><option value="CL1500:DYNA20">CL1500 DYNA20</option></select></Field>
                  <Field label="Lashing sertifikalı MSL" unit="N"><input type="number" min="0" step="100" disabled={!!form.cordstrap_product} className={inputClass} {...numeric('direct_lashing_msl_n')} /></Field>
                  <Field label="Yük bağlantısı MSL" unit="N"><input type="number" min="0" step="100" className={inputClass} {...numeric('cargo_attachment_msl_n')} /></Field>
                  <Field label="CTU bağlantısı MSL" unit="N"><input type="number" min="0" step="100" className={inputClass} {...numeric('ctu_attachment_msl_n')} /></Field>
                  <Field label="Dikey açı" unit="°"><input type="number" min="30" max="60" step="1" className={inputClass} {...numeric('direct_lashing_vertical_angle_deg')} /></Field>
                  <Field label="Yatay açı" unit="°"><input type="number" min="0" max="90" step="1" className={inputClass} {...numeric('direct_lashing_horizontal_angle_deg')} /></Field>
                  <Field label="Yük bağlantı yüksekliği" unit="m"><input type="number" min="0.01" step="0.01" className={inputClass} {...numeric('direct_lashing_attachment_height_m')} /></Field>
                  <Field label="Devrilme eksenine mesafe" unit="m"><input type="number" min="0" step="0.01" className={inputClass} {...numeric('direct_lashing_attachment_lever_m')} /></Field>
                </div>
              </section>

              <section>
                <h3 className="mb-3 text-xs font-bold uppercase tracking-normal text-orange-600">Geometri</h3>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Dış çap" unit="m"><input type="number" min="0.01" step="0.01" className={inputClass} {...numeric('outer_diameter_m')} /></Field>
                  <Field label="İç çap" unit="m"><input type="number" min="0.01" step="0.01" className={inputClass} {...numeric('inner_diameter_m')} /></Field>
                  <Field label="Coil genişliği" unit="m"><input type="number" min="0.01" step="0.01" className={inputClass} {...numeric('coil_width_m')} /></Field>
                  <Field label="Sürtünme yüzeyi"><select className={inputClass} value={form.friction_surface} onChange={e => setValue('friction_surface', e.target.value)}><option value="">Manuel katsayı</option><option value="metal_metal">Metal - metal</option><option value="metal_wood">Metal - ahşap</option><option value="wood_wood">Ahşap - ahşap</option><option value="asm">Kaymaz malzeme (0,60)</option><option value="asm_06">Kaymaz malzeme (0,45)</option></select></Field>
                  <Field label="Sürtünme katsayısı"><select disabled={!!form.friction_surface} className={inputClass} value={form.friction_coefficient} onChange={e => setValue('friction_coefficient', Number(e.target.value))}>{[0, .05, .1, .15, .2, .25, .3, .35, .4, .45].map(value => <option key={value} value={value}>{value.toFixed(2)}</option>)}</select></Field>
                  <Field label="Bedding açıklığı" unit="m"><input type="number" min="0.05" max="2.25" step="0.05" className={inputClass} {...numeric('beam_spread_m')} /></Field>
                  <Field label="Takoz yüksekliği" unit="m"><input type="number" min="0.01" step="0.01" className={inputClass} {...numeric('provided_chock_height_m')} /></Field>
                  <Field label="Alt blok yüksekliği" unit="m"><input type="number" min="0" step="0.01" className={inputClass} {...numeric('provided_bottom_blocking_height_m')} /></Field>
                </div>
              </section>

              <section>
                <h3 className="mb-3 text-xs font-bold uppercase tracking-normal text-orange-600">Ağırlık merkezi ve half-loop</h3>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="CoG yüksekliği" unit="m"><input type="number" min="0.01" step="0.01" disabled={form.use_geometric_cog} className={inputClass} value={form.use_geometric_cog ? (form.outer_diameter_m / 2).toFixed(2) : form.cog_vertical_distance_m} onChange={e => setValue('cog_vertical_distance_m', Number(e.target.value))} /></Field>
                  <Field label="Boyuna destek mesafesi" unit="m"><input type="number" min="0.01" step="0.01" disabled={form.use_geometric_cog} className={inputClass} value={form.use_geometric_cog ? (form.outer_diameter_m / 2).toFixed(2) : form.cog_longitudinal_distance_m} onChange={e => setValue('cog_longitudinal_distance_m', Number(e.target.value))} /></Field>
                  <Field label="Enine destek mesafesi" unit="m"><input type="number" min="0.01" step="0.01" disabled={form.use_geometric_cog} className={inputClass} value={form.use_geometric_cog ? (form.outer_diameter_m / 2).toFixed(2) : form.cog_transverse_distance_m} onChange={e => setValue('cog_transverse_distance_m', Number(e.target.value))} /></Field>
                  <Field label="Half-loop çift sayısı"><input type="number" min="0" step="1" className={inputClass} {...numeric('half_loop_pair_count')} /></Field>
                  <div className="col-span-2"><Field label="Sertifikalı MSL / lashing" unit="N"><input type="number" min="100" step="100" disabled={!!form.cordstrap_product} className={inputClass} {...numeric('certified_half_loop_msl_per_lashing_n')} /></Field></div>
                  {form.cordstrap_product && <p className="col-span-2 text-[11px] text-slate-500">MSL değerleri seçili Cordstrap ürün tablosundan alınır; ürün/ünite uyumluluğu API tarafından doğrulanır.</p>}
                </div>
                <div className="mt-3 grid gap-2 text-xs sm:grid-cols-2">
                  <label className="flex items-center gap-2"><input type="checkbox" checked={form.use_geometric_cog} onChange={e => setValue('use_geometric_cog', e.target.checked)} /> CoG: geometrik merkez varsayımı</label>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={form.lashings_prevent_roll_over} onChange={e => setValue('lashings_prevent_roll_over', e.target.checked)} /> Lashing rollover önlüyor</label>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={form.lashings_prevent_climbing} onChange={e => setValue('lashings_prevent_climbing', e.target.checked)} /> Lashing tırmanmayı önlüyor</label>
                </div>
              </section>

              <section>
                <h3 className="mb-3 text-xs font-bold uppercase tracking-normal text-orange-600">Yapısal bloklama</h3>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Blok genişliği" unit="cm"><input type="number" min="0.1" step="0.1" className={inputClass} {...numeric('blocking_block_width_cm')} /></Field>
                  <Field label="Blok yüksekliği" unit="cm"><input type="number" min="0.1" step="0.1" className={inputClass} {...numeric('blocking_block_height_cm')} /></Field>
                  <Field label="İleri blok adedi"><input type="number" min="0" step="1" className={inputClass} {...numeric('blocking_count_forward')} /></Field>
                  <Field label="Geri blok adedi"><input type="number" min="0" step="1" className={inputClass} {...numeric('blocking_count_rearward')} /></Field>
                  <Field label="Sol blok adedi"><input type="number" min="0" step="1" className={inputClass} {...numeric('blocking_count_left')} /></Field>
                  <Field label="Sağ blok adedi"><input type="number" min="0" step="1" className={inputClass} {...numeric('blocking_count_right')} /></Field>
                  <Field label="İzin verilen kesit gerilmesi" unit="kN/cm²"><input type="number" min="0" step="0.01" placeholder="Doğrulanmamış" className={inputClass} {...numeric('allowable_section_stress_kn_per_cm2')} /></Field>
                  <Field label="Bağlantı kapasitesi / blok" unit="kN"><input type="number" min="0" step="0.1" placeholder="Doğrulanmamış" className={inputClass} {...numeric('attachment_capacity_kn_per_block')} /></Field>
                </div>
              </section>

              {(localError || error) && <div className="flex gap-2 rounded-md border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700 dark:border-rose-800 dark:bg-rose-950/30 dark:text-rose-300"><AlertTriangle className="h-4 w-4 shrink-0" />{localError || error}</div>}
              <button type="submit" disabled={loading || !!localError} className="flex h-11 w-full items-center justify-center gap-2 rounded-md bg-orange-600 text-sm font-bold text-white hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-50">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calculator className="h-4 w-4" />} Hesapla
              </button>
            </div>
          )}
        </form>

        <div className="min-w-0 space-y-5">
          <SteelCoilTechnicalDrawing
            axis={form.coil_axis as 'longitudinal' | 'transverse'}
            outerDiameterM={form.outer_diameter_m}
            innerDiameterM={form.inner_diameter_m}
            coilWidthM={form.coil_width_m}
            beamSpreadM={form.beam_spread_m}
            providedChockHeightM={form.provided_chock_height_m}
            cogHeightM={form.cog_vertical_distance_m}
            requiredBeddingLengthM={result?.bedding.required_length_m}
            requiredChockHeightM={result?.chock.required_height_m}
            chockPasses={result?.chock.geometry_passes}
            directLashingCount={Math.max(form.direct_lashing_count_forward, form.direct_lashing_count_rearward, form.direct_lashing_count_left, form.direct_lashing_count_right)}
            directLashingAttachmentHeightM={form.direct_lashing_attachment_height_m}
            directLashingVerticalAngleDeg={form.direct_lashing_vertical_angle_deg}
            directLashingHorizontalAngleDeg={form.direct_lashing_horizontal_angle_deg}
          />
          <SteelCoil3DCard
            axis={form.coil_axis as 'longitudinal' | 'transverse'}
            outerDiameterM={form.outer_diameter_m}
            innerDiameterM={form.inner_diameter_m}
            coilWidthM={form.coil_width_m}
            beamSpreadM={form.beam_spread_m}
            beddingLengthM={result?.bedding.required_length_m || Math.max(form.coil_width_m, 1.2)}
            chockHeightM={form.provided_chock_height_m}
            cogHeightM={form.cog_vertical_distance_m}
            halfLoopPairCount={form.half_loop_pair_count}
            chockPasses={result?.chock.geometry_passes}
            assessmentPasses={assessmentPasses}
            directLashingCount={Math.max(form.direct_lashing_count_forward, form.direct_lashing_count_rearward, form.direct_lashing_count_left, form.direct_lashing_count_right)}
            directLashingAttachmentHeightM={form.direct_lashing_attachment_height_m}
            directLashingVerticalAngleDeg={form.direct_lashing_vertical_angle_deg}
            directLashingHorizontalAngleDeg={form.direct_lashing_horizontal_angle_deg}
          />
          {!result ? (
            <div className="grid min-h-[180px] place-items-center rounded-lg border border-dashed border-slate-300 bg-white/50 p-8 text-center dark:border-slate-700 dark:bg-[#182235]/50">
              <div><Disc3 className="mx-auto mb-3 h-10 w-10 text-slate-300" /><h2 className="text-base font-bold tracking-normal !text-slate-800 dark:!text-white">Hesaplama sonucu bekleniyor</h2><p className="mt-1 text-xs text-slate-500">Sonuçlar bedding, bloklama ve dört yön için birlikte gösterilir.</p></div>
            </div>
          ) : (
            <>
              <section className={`flex flex-col gap-4 border-y px-4 py-4 sm:flex-row sm:items-center sm:justify-between ${assessmentPasses ? 'border-emerald-300 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/25' : 'border-rose-300 bg-rose-50 dark:border-rose-800 dark:bg-rose-950/25'}`}>
                <div className="flex min-w-0 items-start gap-3">
                  {assessmentPasses ? <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-emerald-600" /> : <ShieldAlert className="mt-0.5 h-6 w-6 shrink-0 text-rose-600" />}
                  <div>
                    <h2 className={`text-sm font-bold tracking-normal ${assessmentPasses ? '!text-emerald-900 dark:!text-emerald-200' : '!text-rose-900 dark:!text-rose-200'}`}>{assessmentPasses ? 'Basitleştirilmiş değerlendirme geçti' : 'Ek sabitleme ve mühendislik incelemesi gerekli'}</h2>
                    <p className={`mt-1 text-xs ${assessmentPasses ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'}`}>{assessmentPasses ? 'Girilen veriler için tüm yön kontrolleri geçti.' : `${failedDirectionCount} yönde kayma veya devrilme kontrolü ek önlem gerektiriyor.`}</p>
                  </div>
                </div>
                <button type="button" onClick={downloadPdf} disabled={pdfLoading} className="flex h-10 shrink-0 items-center justify-center gap-2 rounded-md bg-slate-900 px-4 text-xs font-bold text-white hover:bg-slate-800 disabled:opacity-50 dark:bg-orange-600 dark:hover:bg-orange-700">
                  {pdfLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} PDF raporunu indir
                </button>
              </section>
              <section className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-[#182235]"><span className="text-xs text-slate-500">Minimum bedding</span><strong className="mt-1 block text-2xl font-bold text-orange-600">{result.bedding.minimum_length_cm} cm</strong><span className="text-[11px] text-slate-400">{result.bedding.governing_check.replaceAll('_', ' ')}</span></div>
                <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-[#182235]"><span className="text-xs text-slate-500">Takoz geometrisi</span><strong className={`mt-2 flex items-center gap-2 text-base ${result.chock.geometry_passes ? 'text-emerald-600' : 'text-rose-600'}`}>{result.chock.geometry_passes ? <CheckCircle2 className="h-5 w-5" /> : <ShieldAlert className="h-5 w-5" />}{result.chock.geometry_passes ? 'Geçti' : 'Yetersiz'}</strong><span className="text-[11px] text-slate-400">Gerekli {(result.chock.required_height_m * 100).toFixed(0)} cm</span></div>
                <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-[#182235]"><span className="text-xs text-slate-500">Alt bloklama</span><strong className={`mt-2 flex items-center gap-2 text-base ${result.bottom_blocking.geometry_passes ? 'text-emerald-600' : 'text-rose-600'}`}>{result.bottom_blocking.geometry_passes ? <CheckCircle2 className="h-5 w-5" /> : <ShieldAlert className="h-5 w-5" />}{result.bottom_blocking.geometry_passes ? 'Geçti' : 'Yetersiz'}</strong><span className="text-[11px] text-slate-400">Gerekli {(result.bottom_blocking.required_height_m * 100).toFixed(0)} cm</span></div>
              </section>

              <section className="overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-[#182235]">
                <div className="border-b border-slate-200 px-4 py-3 dark:border-slate-700"><h2 className="text-sm font-bold tracking-normal !text-slate-900 dark:!text-white">Yön bazlı kuvvet değerlendirmesi</h2></div>
                <div className="overflow-x-auto"><table className="w-full min-w-[1160px] text-left text-xs"><thead className="bg-slate-50 text-slate-500 dark:bg-slate-800/60"><tr><th className="px-4 py-3">Yön</th><th className="px-4 py-3">Dış kuvvet</th><th className="px-4 py-3">Sürtünme</th><th className="px-4 py-3">Kalan ihtiyaç</th><th className="px-4 py-3">Doğrudan lashing</th><th className="px-4 py-3">Half-loop</th><th className="px-4 py-3">Bloklama</th><th className="px-4 py-3">Kayma</th><th className="px-4 py-3">Devrilme</th></tr></thead><tbody>{result.directions.map(item => <tr key={item.direction} className="border-t border-slate-100 dark:border-slate-700"><td className="px-4 py-3 font-bold">{labels[item.direction]}</td><td className="px-4 py-3 font-mono">{(item.force.external_force_n / 1000).toFixed(1)} kN</td><td className="px-4 py-3 font-mono">{(item.force.friction_resistance_n / 1000).toFixed(1)} kN</td><td className="px-4 py-3 font-mono">{(item.force.remaining_force_n / 1000).toFixed(1)} kN</td><td className={`px-4 py-3 font-semibold ${item.direct_lashing?.passes ? 'text-emerald-600' : item.direct_lashing ? 'text-rose-600' : 'text-slate-400'}`}>{item.direct_lashing ? item.direct_lashing.capacity_verified ? `${((item.direct_lashing.total_directional_capacity_n || 0) / 1000).toFixed(1)} kN · %${((item.direct_lashing.utilization || 0) * 100).toFixed(0)}` : 'Doğrulanmamış' : 'Kullanılmıyor'}</td><td className="px-4 py-3 font-mono">{item.half_loop_capacity_t.toFixed(1)} t</td><td className={`px-4 py-3 font-semibold ${item.structural_blocking?.passes ? 'text-emerald-600' : item.structural_blocking ? 'text-rose-600' : 'text-slate-400'}`}>{item.structural_blocking ? item.structural_blocking.capacity_verified ? `${item.structural_blocking.total_verified_capacity_kn?.toFixed(1)} kN · %${((item.structural_blocking.utilization || 0) * 100).toFixed(0)}` : 'Doğrulanmamış' : 'Kullanılmıyor'}</td><td className={`px-4 py-3 font-semibold ${item.sliding_status === 'additional_securing_required' ? 'text-rose-600' : 'text-emerald-600'}`}>{statusLabel(item.sliding_status)}</td><td className={`px-4 py-3 font-semibold ${item.tipping_status === 'additional_securing_required' ? 'text-rose-600' : 'text-emerald-600'}`}>{item.tipping_status === 'direct_lashing_tipping_pass' ? `Lashing yeterli · %${((item.direct_lashing_tipping?.utilization || 0) * 100).toFixed(0)}` : item.tipping_status === 'additional_securing_required' ? 'Sabitleme gerekli' : 'Doğal kararlı'}</td></tr>)}</tbody></table></div>
              </section>

              {result.warnings.length > 0 && <section className="rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-950/25"><h2 className="flex items-center gap-2 text-sm font-bold text-amber-900 dark:text-amber-200"><AlertTriangle className="h-4 w-4" />Mühendislik uyarıları</h2><ul className="mt-2 space-y-1 text-xs text-amber-800 dark:text-amber-300">{result.warnings.map(warning => <li key={warning}>• {warning}</li>)}</ul></section>}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
